import React from "react";

// ── Pure helper functions for Team Chat ──────────────────────────────────────

// Highlight matching text with yellow background
export function highlightText(text: string, query: string): React.ReactNode[] {
  if (!query || !query.trim()) return [text];
  const regex = new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
  const parts = text.split(regex);
  return parts.map((part, i) =>
    regex.test(part)
      ? React.createElement('mark', { key: i, className: 'bg-amber-200 dark:bg-amber-700/50 text-inherit rounded-sm px-0.5' }, part)
      : part
  );
}

export function getInitials(name: string) {
  return name?.split(" ").map(n => n[0]).join("").slice(0, 2).toUpperCase() || "?";
}

export function formatTime(dateStr: string) {
  const d = new Date(dateStr);
  const now = new Date();
  const isToday = d.toDateString() === now.toDateString();
  if (isToday) return d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  const yesterday = new Date(now); yesterday.setDate(now.getDate() - 1);
  if (d.toDateString() === yesterday.toDateString()) return `Yesterday ${d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}`;
  return `${d.toLocaleDateString([], { month: "short", day: "numeric" })} ${d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}`;
}

export function statusColor(status?: string, isOnline?: boolean) {
  if (status === "on-call" || status === "on_call") return "bg-blue-500";
  if (status === "dnd") return "bg-rose-500";
  if (status === "idle") return "bg-amber-400";
  if (isOnline || status === "available" || status === "online") return "bg-emerald-500";
  return "bg-zinc-400";
}

export function statusLabel(status?: string, isOnline?: boolean) {
  if (status === "on-call" || status === "on_call") return "On Call";
  if (status === "dnd") return "Do Not Disturb";
  if (status === "idle") return "Idle";
  if (isOnline || status === "available" || status === "online") return "Online";
  return "Offline";
}

// Simple markdown-like formatting for chat messages
export function formatMessageText(text: string): React.ReactNode[] {
  const parts: React.ReactNode[] = [];
  let remaining = text;
  let key = 0;

  const patterns: [RegExp, (match: string, content: string) => React.ReactNode][] = [
    [/```([^`]+)```/, (_m, c) => React.createElement("code", { key: key++, className: "block bg-zinc-100 dark:bg-zinc-800 rounded-md px-3 py-2 text-xs font-mono text-zinc-700 dark:text-zinc-300 my-1 whitespace-pre-wrap" }, c)],
    [/`([^`]+)`/, (_m, c) => React.createElement("code", { key: key++, className: "bg-zinc-100 dark:bg-zinc-800 rounded px-1.5 py-0.5 text-xs font-mono text-pink-600 dark:text-pink-400" }, c)],
    [/\*\*([^*]+)\*\*/, (_m, c) => React.createElement("strong", { key: key++, className: "font-bold" }, c)],
    [/\*([^*]+)\*/, (_m, c) => React.createElement("em", { key: key++ }, c)],
    [/~~([^~]+)~~/, (_m, c) => React.createElement("del", { key: key++, className: "line-through text-zinc-400" }, c)],
  ];

  while (remaining.length > 0) {
    let earliestMatch: { index: number; fullMatch: string; content: string; render: (m: string, c: string) => React.ReactNode } | null = null;

    for (const [pattern, render] of patterns) {
      const match = remaining.match(pattern);
      if (match && match.index !== undefined) {
        if (!earliestMatch || match.index < earliestMatch.index) {
          earliestMatch = { index: match.index, fullMatch: match[0], content: match[1], render };
        }
      }
    }

    if (earliestMatch) {
      if (earliestMatch.index > 0) {
        parts.push(remaining.substring(0, earliestMatch.index));
      }
      parts.push(earliestMatch.render(earliestMatch.fullMatch, earliestMatch.content));
      remaining = remaining.substring(earliestMatch.index + earliestMatch.fullMatch.length);
    } else {
      parts.push(remaining);
      break;
    }
  }

  return parts;
}

// Detect and render URLs as clickable links
export function renderWithLinks(text: string): React.ReactNode[] {
  const urlRegex = /(https?:\/\/[^\s<]+)/g;
  const parts = text.split(urlRegex);
  return parts.map((part, i) => {
    if (part.match(urlRegex)) {
      return React.createElement("a", {
        key: i,
        href: part,
        target: "_blank",
        rel: "noopener noreferrer",
        className: "text-blue-600 dark:text-blue-400 hover:underline break-all",
      }, part.length > 60 ? part.substring(0, 57) + "..." : part);
    }
    return part;
  });
}

// ── GIF / sticker favorites ──────────────────────────────────────────────────

export const GIF_FAVORITES_KEY = "team-chat-gif-favorites";
const STICKER_FAVORITES_KEY = "team-chat-sticker-favorites";
const MEME_FAVORITES_KEY = "team-chat-meme-favorites";
const CLIP_FAVORITES_KEY = "team-chat-clip-favorites";

type FavoriteMedia = { id: string; url: string; preview: string };
type FavoriteKind = "gifs" | "stickers" | "memes" | "clips";

// Stickers keep their own list so a sticker never turns up in the GIF tab.
function favoritesKey(kind: FavoriteKind = "gifs") {
  if (kind === "stickers") return STICKER_FAVORITES_KEY;
  if (kind === "memes") return MEME_FAVORITES_KEY;
  if (kind === "clips") return CLIP_FAVORITES_KEY;
  return GIF_FAVORITES_KEY;
}

export function getGifFavorites(kind: FavoriteKind = "gifs"): FavoriteMedia[] {
  try {
    const stored = localStorage.getItem(favoritesKey(kind));
    return stored ? JSON.parse(stored) : [];
  } catch { return []; }
}

export function toggleGifFavorite(gif: FavoriteMedia, kind: FavoriteKind = "gifs") {
  const favs = getGifFavorites(kind);
  const exists = favs.findIndex(f => f.id === gif.id);
  if (exists >= 0) {
    favs.splice(exists, 1);
  } else {
    favs.unshift(gif);
    if (favs.length > 50) favs.pop(); // Cap at 50 favorites
  }
  try {
    localStorage.setItem(favoritesKey(kind), JSON.stringify(favs));
  } catch {
    // private mode / blocked storage — the in-memory list still updates.
  }
  return favs;
}

/**
 * Dispatched by media in a message once it has decoded and taken up its real
 * height. The message list listens for it on the scroll container and re-pins
 * to the bottom, because the growth happens AFTER the scroll that was supposed
 * to bring the message into view.
 */
export const MEDIA_LOADED_EVENT = "team-chat:media-loaded";
